/**
 * The lower western quarter (lifted from lantern-water's `backdrop-west.js`,
 * built by `world.js` `westernGarden`): a lane of houses facing the rail
 * across a strip of walled back yards, a taller back rank, tree masses on the
 * meadow beyond, lane shade, gates, a wall stair, washing, woodpiles, a well,
 * vegetable plots and a hay cart. The camera sees the houses' east fronts and
 * south gables from the west edge of town. A world-coordinate set piece:
 * placed at the origin. Its own seed (60217); nothing here is walkable. No
 * params.
 */
import * as THREE from 'three';
import { atlasMesh, backdropMaterial, type V3 } from '../backdrop-art.ts';
import { BAY, type RoofKind, type Style, TINTS, createBackdropBuilder } from '../backdrop-kit.ts';
import { createBackdropProps } from '../backdrop-props.ts';
import type { Prefab } from './types.ts';

export const prefab: Prefab = {
  id: 'backdrop_west',
  buildPrefab(ctx) {
    const p = new THREE.Group();
    p.name = 'backdrop-west';
    const { m, box } = ctx;
    const material = backdropMaterial(ctx.kit, m.windowCool);
    const b = createBackdropBuilder(ctx, 60217);
    const P = createBackdropProps(ctx, b);
    const { house, random } = b;
    const roofOf = (rm: 'roof' | 'slate'): THREE.Material => (rm === 'slate' ? m.roofDark : m.roof);
    // The paving top of the lower quarter.
    const Y = 0.65;
    box(p, -28.6, -0.15, 11, 13.2, 1.5, 22, m.stone);
    box(p, -28.6, 0.62, 11, 13.2, 0.06, 22, m.paving);
    // A sunken service lane between the district quay and the quarter's wall.
    box(p, -21.05, -0.2, -6, 1.9, 0.36, 48, m.paving);

    const houses: readonly (readonly [front: number, z0: number, z1: number, floors: number, style: Style, tint: V3, roof: RoofKind, rm: 'roof' | 'slate'])[] = [
      [-24.6, -29.4, -25.65, 3, 'plaster', TINTS.amber, 'gableX', 'slate'],
      [-25.0, -24.8, -19.8, 3, 'timber', TINTS.straw, 'gableX', 'roof'],
      [-24.7, -19.8, -16.05, 4, 'plaster', TINTS.teal, 'hip', 'slate'],
      [-24.9, -14.9, -11.15, 3, 'brick', TINTS.warm, 'gableX', 'roof'],
      [-24.6, -11.15, -8.65, 3, 'plaster', TINTS.rust, 'gableZ', 'slate'],
      [-25.2, -7.6, -3.85, 2, 'timber', TINTS.ochre, 'gableX', 'roof'],
      [-24.8, -3.85, -1.35, 3, 'plaster', TINTS.sage, 'hip', 'roof'],
      [-26.1, 1.2, 4.95, 2, 'plaster', TINTS.straw, 'gableX', 'roof'],
      [-26.3, 6.0, 8.5, 3, 'timber', TINTS.rust, 'gableZ', 'slate'],
      [-26.0, 8.5, 12.25, 2, 'brick', TINTS.warm, 'hip', 'slate'],
      [-26.2, 13.35, 17.1, 3, 'plaster', TINTS.ochre, 'gableX', 'roof'],
      [-26.1, 17.1, 19.6, 2, 'plaster', TINTS.sage, 'gableZ', 'slate'],
    ];
    for (const [front, z0, z1, floors, style, tint, roof, rm] of houses) {
      const deep = Math.round((z1 - z0) / BAY);
      house(p, { x: front - 1.5 * BAY, z: (z0 + z1) / 2, bays: 3, deep: (z1 - z0) / BAY, floors, base: Y, style, tint, roof, roofMat: roofOf(rm), face: 'east', side: 'south', passage: deep >= 3, lit: 0.14 });
    }
    // A taller back rank (and one more on the far plinth): another roofline above the lane's.
    const back: readonly (readonly [z0: number, z1: number, floors: number, tint: V3, roof: RoofKind, rm: 'roof' | 'slate', xc: number, base: number])[] = [
      [-28.6, -24.6, 5, TINTS.umber, 'gableX', 'slate', -32.6, Y],
      [-23.8, -19.8, 4, TINTS.rust, 'gableZ', 'roof', -32.2, Y],
      [-18.6, -13.6, 5, TINTS.sage, 'gableX', 'roof', -32.8, Y],
      [-12.4, -8.4, 4, TINTS.amber, 'gableX', 'slate', -32.4, Y],
      [-7.2, -2.2, 5, TINTS.teal, 'gableZ', 'roof', -32.6, Y],
      [-1.0, 3.0, 4, TINTS.clay, 'gableX', 'roof', -32.9, Y],
      [4.2, 8.2, 4, TINTS.ochre, 'gableX', 'slate', -32.5, Y],
      [9.4, 14.4, 5, TINTS.umber, 'gableZ', 'roof', -32.7, Y],
      [15.6, 19.6, 4, TINTS.rust, 'gableX', 'slate', -32.4, Y],
      [-33.5, -28.5, 5, TINTS.sage, 'gableX', 'roof', -40.5, 0.24],
      [-27.2, -22.2, 6, TINTS.ochre, 'gableZ', 'slate', -40.0, 0.24],
      [-20.8, -15.8, 5, TINTS.rust, 'gableX', 'roof', -40.6, 0.24],
    ];
    for (const [z0, z1, floors, tint, roof, rm, xc, base] of back) {
      house(p, { x: xc, z: (z0 + z1) / 2, bays: 3, deep: (z1 - z0) / BAY, floors, base, style: 'plaster', tint, roof, roofMat: roofOf(rm), face: 'east', side: 'south', cheap: true, dormers: false, door: false });
    }
    // Dark tree masses on the meadow behind the lane close the horizon.
    for (let z = -12; z < 26; z += 2.6) for (const dx of [0, 2.4]) P.tree(p, -37.4 - dx + (random() - 0.5) * 1.2, -0.05, z + dx * 0.5, 1.35 + random() * 0.35, random() < 0.2);
    // Shadowed gaps between houses read as lanes running deeper into the quarter.
    for (const [z0, z1] of [
      [-25.65, -24.8],
      [-16.05, -14.9],
      [-8.65, -7.6],
      [-1.35, 1.2],
      [4.95, 6.0],
      [12.25, 13.35],
    ] as const)
      b.kit.quad([-29.5, Y, z0], [-29.5, Y, z1], [-29.5, Y + 5.2, z1], [-29.5, Y + 5.2, z0], 42, [1, 1, 1], [0, 0, 1, 1], [0.7, 1]);

    // The yard strip in the northern half (x −22.2 … −24.5).
    for (const [z0, z1] of [
      [-30, -23.4],
      [-21.8, -13.2],
      [-11.6, -5.4],
      [-3.6, 0.4],
    ] as const)
      P.lowWall(p, -22.2, z0, -22.2, z1, Y, 0.55);
    P.gate(p, -22.2, Y, -22.6, 1.1, 'z');
    P.gate(p, -22.2, Y, -12.4, 1.1, 'z');
    // Steps from the lower lane up into the quarter (never reachable).
    P.wallStair(p, -20.5, -0.1, -4.5, 3, -1, 'x', 0.5, 0.25, 1.1);
    P.laundry(p, -23.0, -29.2, -23.0, -26.0, Y, 4);
    P.barrel(p, -23.9, Y, -21.0);
    P.barrel(p, -23.4, Y, -20.5, 0.36);
    P.crate(p, -23.7, Y, -19.6);
    P.woodpile(p, -24.0, Y, -17.2, 1.5, 'east');
    P.laundry(p, -23.1, -14.5, -23.1, -11.6, Y, 3);
    P.bush(p, -23.3, Y, -10.2, 0.9);
    P.vegPlot(p, -23.4, Y, -7.0, 1.6, 2.4);
    P.tree(p, -23.2, Y, -3.1, 0.85);
    P.barrel(p, -23.9, Y, -0.6);

    // Deeper back yards in the southern half (x −22.3 … −26.0).
    for (const z of [0.4, 5.5, 8.5, 12.8, 17.2]) P.lowWall(p, -22.3, z, -25.9, z, Y, 0.5);
    for (const [z0, z1] of [
      [0.4, 2.3],
      [3.7, 13.9],
      [15.3, 21.6],
    ] as const)
      P.lowWall(p, -22.3, z0, -22.3, z1, Y, 0.55);
    P.gate(p, -22.3, Y, 3.0, 1.1, 'z');
    P.gate(p, -22.3, Y, 14.6, 1.1, 'z');
    // Yard A: a kitchen garden with bean canes.
    P.vegPlot(p, -24.2, Y, 2.9, 2.6, 3.4);
    P.bush(p, -22.9, Y, 1.2, 0.7);
    // Yard B: a washing line and a basket of pegs.
    P.laundry(p, -23.0, 5.9, -25.4, 8.1, Y, 4);
    P.crate(p, -25.3, Y, 6.3, 0.45);
    // Yard C: a woodshed, a well and barrels.
    P.woodpile(p, -25.3, Y, 11.0, 1.8, 'east');
    P.well(p, -23.6, Y, 10.3);
    P.barrel(p, -25.2, Y, 9.0);
    P.barrel(p, -24.7, Y, 8.9, 0.36);
    // Yard D: a second plot and a pear tree.
    P.vegPlot(p, -24.6, Y, 15.9, 2.0, 2.4, 43);
    P.tree(p, -23.1, Y, 16.4, 0.95, true);
    P.laundry(p, -22.9, 13.3, -25.4, 13.3, Y, 3);
    // Yard E: an open corner with a hay cart, a hedge and an apple tree.
    P.cart(p, -24.0, Y, 20.2, 0.4);
    P.hedge(p, -24.1, Y, 21.7, 3.6, 0.7, 0.9);
    P.tree(p, -29.6, Y, 21.0, 1.1);
    P.shed(p, -26.9, Y, 21.0, 1.8, 1.3, 'south');

    atlasMesh(p, b.kit, material, 'backdrop-west-atlas');
    const root = new THREE.Group();
    root.add(p);
    return { root };
  },
};
